import { useState, useMemo, lazy, Suspense } from "react";
import { useParams, Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { DocumentText, FolderOpen, ClipboardText, Personalcard, Edit2, Airplane } from "iconsax-react";
import { Badge, Button, Toast } from "@/shared/ui";
import { useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { HR_EMPLOYEES_PAGE_ROUTE } from "@/shared/utils";
import { getActiveDataChangeRequest } from "@/features/self/requests/utils/requestRestrictions";
import { STATUS_MAP } from "../../consts/statuses";
import { useGetWorkerDetailQuery } from "../../api";
import type { WorkerDetailTabId } from "./types";
import { ContractTab, DocumentsTab, RequestsTab, WorkerDetailSkeleton, PersonalSheetTab } from "./components";

const BusinessTripModal = lazy(() =>
  import("./components/BusinessTripModal").then((m) => ({ default: m.default })),
);

export default function WorkerDetailPage() {
  const { t } = useTranslation("WorkerDetailPage");
  const { t: tEmployee } = useTranslation("EmployeePortal");
  const { id } = useParams<{ id: string }>();
  const { hasPermission, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState<WorkerDetailTabId>("personal_sheet");
  const [isPersonalSheetEdit, setIsPersonalSheetEdit] = useState(false);
  const [showBusinessTripModal, setShowBusinessTripModal] = useState(false);

  const numericId = Number(id);
  const isInvalidId = !id || isNaN(numericId);

  const {
    data: worker,
    isLoading,
    error,
  } = useGetWorkerDetailQuery(numericId, {
    skip: isInvalidId,
  });

  useBreadcrumbOverrides(worker ? { workerName: worker.full_name } : {});

  const hasApproveDataChange = hasPermission(PERMISSIONS.HR.APPROVE_DATA_CHANGE);
  const isChiefApprover = hasRole("chief") || hasPermission(PERMISSIONS.HR.APPROVE_DATA_CHANGE_CHIEF);

  const activeDataChangeRequest = useMemo(function computeActiveRequest() {
    const dataChangeRequests = worker?.requests?.filter((r) => r.type === "data_change_request");
    return getActiveDataChangeRequest(dataChangeRequests);
  }, [worker?.requests]);

  const canEditPersonalSheet = (hasApproveDataChange || isChiefApprover) && activeDataChangeRequest === null;
  const canCreateBusinessTrip = hasRole("hr") || hasRole("chief");

  const tabs = [
    { id: "personal_sheet" as const, label: t("tabs.personal_sheet"), icon: Personalcard },
    { id: "contract" as const, label: t("tabs.contract"), icon: DocumentText },
    { id: "documents" as const, label: t("tabs.documents"), icon: FolderOpen, count: worker?.documents?.length ?? 0 },
    { id: "requests" as const, label: t("tabs.requests"), icon: ClipboardText, count: worker?.requests?.length ?? 0 },
  ];

  if (isInvalidId) {
    return <Navigate to={HR_EMPLOYEES_PAGE_ROUTE} replace />;
  }

  if (isLoading && !worker) {
    return (
      <>
        <title>{t("meta.title", { name: "..." })}</title>
        <section className="flex-1 min-w-0 flex flex-col gap-6">
          <WorkerDetailSkeleton />
        </section>
      </>
    );
  }

  if (error || !worker) {
    return <Navigate to={HR_EMPLOYEES_PAGE_ROUTE} replace />;
  }

  const statusConfig = STATUS_MAP[worker.status as keyof typeof STATUS_MAP];
  const initials = `${worker.surname?.[0] ?? ""}${worker.name?.[0] ?? ""}`;

  function handleTabChange(tabId: string) {
    setActiveTab(tabId as WorkerDetailTabId);
    if (tabId !== "personal_sheet") setIsPersonalSheetEdit(false);
  }

  function renderTabContent() {
    switch (activeTab) {
      case "personal_sheet":
        return worker!.personal_sheet ? (
          <PersonalSheetTab
            personalSheet={worker!.personal_sheet}
            workerId={numericId}
            isEditMode={isPersonalSheetEdit}
            onEditModeChange={setIsPersonalSheetEdit}
            isChiefDirectEdit={isChiefApprover}
          />
        ) : null;
      case "contract":
        return <ContractTab worker={worker!} />;
      case "documents":
        return <DocumentsTab worker={worker!} />;
      case "requests":
        return <RequestsTab worker={worker!} />;
      default:
        return null;
    }
  }

  return (
    <>
      <title>{t("meta.title", { name: worker.full_name })}</title>
      <section className="flex-1 min-w-0 flex flex-col gap-6">
        <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-4">
          <div className="flex items-center gap-4">
            {worker.photo_url ? (
              <img src={worker.photo_url} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full surface-brand-subtle flex items-center justify-center text-display-2xs content-action-brand">
                {initials}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <h1 className="text-heading-lg content-base-primary">{worker.full_name}</h1>
                {statusConfig && (
                  <Badge
                    variant="soft"
                    color={statusConfig.color}
                    text={t(statusConfig.label)}
                    icon={statusConfig.icon}
                  />
                )}
              </div>
              <p className="text-body-regular-md content-base-secondary">
                {t("personal.iin")}: {worker.iin}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 radius-3xl surface-base-fill flex flex-wrap items-center gap-2">
          {tabs.map(function renderTab(tab) {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  "px-3 py-2.5 flex justify-start items-center gap-2 radius-sm text-label-md cursor-pointer",
                  isActive ? "background-brand-fill text-white" : "surface-component-fill content-action-neutral",
                )}>
                <Icon size={20} color="currentColor" />
                {tab.label}
                {"count" in tab && tab.count != null && tab.count > 0 && (
                  <span
                    className={cn(
                      "min-w-5 h-5 px-1.5 flex items-center justify-center rounded-md text-label-xs",
                      isActive
                        ? "background-static-white content-action-brand"
                        : "surface-neutral-subtle content-base-secondary",
                    )}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            {canCreateBusinessTrip && (
              <Button variant="secondary" size="md" onClick={() => setShowBusinessTripModal(true)}>
                <Airplane size={16} color="currentColor" />
                {t("businessTrip.createButton")}
              </Button>
            )}
            {activeTab === "personal_sheet" && canEditPersonalSheet && !isPersonalSheetEdit && worker.personal_sheet && (
              <Button variant="secondary" size="md" onClick={() => setIsPersonalSheetEdit(true)}>
                <Edit2 size={16} color="currentColor" />
                {tEmployee("personalSheet.edit.button")}
              </Button>
            )}
          </div>
        </div>

        {activeTab === "personal_sheet" &&
          worker.personal_sheet &&
          (hasApproveDataChange || isChiefApprover) &&
          activeDataChangeRequest !== null &&
          !isPersonalSheetEdit && (
            <Toast
              color="notice"
              text={t("personalSheet.activeRequestBanner.title")}
              additionalText={t("personalSheet.activeRequestBanner.text")}
              isFullWidth
              autoClose={false}
              closable={false}
            />
          )}

        {renderTabContent()}
      </section>

      {showBusinessTripModal && (
        <Suspense fallback={null}>
          <BusinessTripModal
            workerId={numericId}
            workerName={worker.full_name}
            onClose={() => setShowBusinessTripModal(false)}
            onSuccess={() => setShowBusinessTripModal(false)}
          />
        </Suspense>
      )}
    </>
  );
}

